import styled, { css, keyframes } from 'styled-components';
import { customMediaQuery } from 'students/views/shared/styled';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

export const SPlayerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  overflow: hidden;
  user-select: none;

  video {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
  }

  video::cue {
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 1rem;
    font-family: inherit;
    line-height: 1.4;
    padding: 0.2em 0.4em;

    ${customMediaQuery('tablet')} {
      font-size: 1.125rem;
    }
  }
`;

export const SBigPlayButton = styled.button<{ $accentColor: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  height: 68px;
  border-radius: 50%;
  border: none;
  background: ${({ $accentColor }) => $accentColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, opacity 0.3s ease;
  z-index: 2;
  padding: 0;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 4px;
  }

  svg {
    width: 28px;
    height: 28px;
    fill: #fff;
    margin-left: 4px;
  }

  ${customMediaQuery('tablet')} {
    width: 80px;
    height: 80px;

    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

export const SControlBar = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  z-index: 3;
  transition: opacity 0.3s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  ${customMediaQuery('tablet')} {
    padding: 10px 16px;
    gap: 10px;
  }

  /*
   * Backstop only (the row is trimmed below the tablet breakpoint, see STimeDuration):
   * wrapping still beats being clipped by the container's overflow: hidden. Playback speed
   * is deliberately NOT hidden on small viewports — slowing a clip to 0.75x is a core
   * comprehension feature and phones are the dominant form factor.
   */
  flex-wrap: wrap;
`;

const controlButtonStyles = css`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

export const SControlButton = styled.button`
  ${controlButtonStyles}
`;

export const STimeDisplay = styled.span`
  color: #fff;
  font-size: 0.8125rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  min-width: max-content;
`;

// " / 1:23" — dropped below the tablet breakpoint: with a 44px touch target per control
// the row overflows a 375px phone, and the duration is the least useful thing in it.
export const STimeDuration = styled.span`
  ${customMediaQuery('mobile')} {
    display: none;
  }
`;

export const SProgressBarContainer = styled.div`
  flex: 1;
  height: 44px;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;

  /* flex: 1 alone lets the seek bar collapse to a few pixels on a crowded
     touch control bar; keep it wide enough to remain seekable. */
  @media (pointer: coarse) {
    min-width: 96px;
  }
`;

export const SProgressBarTrack = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  position: relative;
  transition: height 0.15s ease;

  ${SProgressBarContainer}:hover & {
    height: 6px;
  }
`;

export const SProgressBarBuffer = styled.div<{ $percent: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: rgba(255, 255, 255, 0.4);
  border-radius: inherit;
`;

export const SProgressBarFilled = styled.div<{ $percent: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: var(--player-accent-color, #8dc63f);
  border-radius: inherit;
`;

export const SSpeedSelectorWrapper = styled.div`
  position: relative;
`;

export const SSpeedButton = styled.button`
  ${controlButtonStyles}
  font-size: 0.8125rem;
  font-weight: 600;
  min-width: 44px;
`;

export const SSpeedPopup = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 8px;
  padding: 4px 0;
  min-width: 80px;
  animation: ${fadeIn} 0.15s ease;
  z-index: 10;
`;

export const SSpeedOption = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  color: ${({ $active }) => ($active ? 'var(--player-accent-color, #8dc63f)' : '#fff')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  font-size: 0.875rem;
  cursor: pointer;
  text-align: center;
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -2px;
  }
`;

export const SCaptionsButton = styled.button<{ $active: boolean }>`
  ${controlButtonStyles}

  ${({ $active }) =>
    $active &&
    css`
      border-bottom: 2px solid var(--player-accent-color, #8dc63f);
    `}
`;

export const SVolumeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const SVolumeSlider = styled.input`
  width: 0;
  overflow: hidden;
  transition: width 0.2s ease;
  accent-color: var(--player-accent-color, #8dc63f);
  cursor: pointer;
  height: 4px;

  ${SVolumeWrapper}:hover & {
    width: 80px;
  }

  /* A hover-revealed slider is unusable on touch — the mute button stays. */
  @media (pointer: coarse) {
    display: none;
  }
`;

export const SLoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  z-index: 2;
  /* Purely decorative — must never swallow the tap that starts playback. */
  pointer-events: none;
`;

export const SErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 16px;
  text-align: center;
  background: rgba(0, 0, 0, 0.6);
  z-index: 4;
`;

export const SErrorMessage = styled.p`
  margin: 0;
  color: var(--color-white, #fff);
  font-size: 0.9375rem;

  ${customMediaQuery('tablet')} {
    font-size: 1.125rem;
  }
`;

export const SRetryButton = styled.button`
  ${controlButtonStyles}
  width: 56px;
  height: 56px;
  border: 2px solid var(--color-white, #fff);
  border-radius: 50%;

  svg {
    width: 26px;
    height: 26px;
  }
`;
